/**
 * @copyright Microsoft Corporation. All rights reserved.
 *
 * @file Analytics Stats component
 */

import * as React from 'react';
import { Link } from 'office-ui-fabric-react/lib/Link';
import { ActivityItem } from 'office-ui-fabric-react/lib/components/ActivityItem/index';
import { IActivity, IIncompleteData } from '../../../models/ItemAnalytics';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { mergeStyles, IStyle } from 'office-ui-fabric-react/lib/Styling';
import strings from '../FileHoverCard.resx';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import { SocialBarLikeLink } from './SocialBarLikeLink';
import * as StringHelper from '@ms/utilities-strings/lib/index';
import * as AnalyticsStatsStyle from './AnalyticsStats.scss';

const DATE_NULL_VALUE = new Date('0001-01-01T00:00:00-08:00');

export interface IAnalyticsStatsProps {
  viewCount?: number;
  viewerCount?: number;
  commentCount?: number;
  likeCount?: number;
  activities?: IActivity[];
  inCompleteData?: IIncompleteData;
  withFacePile?: boolean;
  isCompact?: boolean;
  isLink?: boolean;
  itemKey?: string;
  onClick?: (ev: React.MouseEvent<HTMLElement>) => void;
  pageUrl?: string;
  isNotSupported?: boolean;
  disableViewerCount?: boolean;
  ODBOptOutViewerList?: boolean;
}

export interface IAnalyticsStatsStrings {
  view: string;
  views: string;
  viewer: string;
  viewers: string;
  apiError?: string;
  suggestion?: string;
  unsupportMessage?: string;
}

export const AnalyticsStats: React.StatelessComponent<IAnalyticsStatsProps> = (props: IAnalyticsStatsProps) => {
  const {
    viewCount,
    viewerCount,
    commentCount,
    likeCount,
    activities,
    inCompleteData,
    onClick,
    pageUrl,
    isCompact,
    isLink,
    itemKey,
    disableViewerCount = true,
    ODBOptOutViewerList = false
  } = props;

  if (!(viewCount && viewerCount)) {
    return null;
  }

  if (!viewCount || !viewerCount) {
    return (
      <ActivityItem
        className={ `${AnalyticsStatsStyle.msAnalyticsStatsStats}` }
        isCompact={ true }
      />
    );
  }
  const analyticsInComplete = inCompleteData && inCompleteData.missingDataBeforeDateTime &&
    (new Date(inCompleteData.missingDataBeforeDateTime).getTime() !== DATE_NULL_VALUE.getTime());
  const wasThrottled = inCompleteData && inCompleteData.wasThrottled;
  const InfoMessage = analyticsInComplete ? strings.inCompleteMessage : (wasThrottled ? strings.throttledStateMessage : '');
  const teachingMessage = disableViewerCount ? (ODBOptOutViewerList ? strings.odbTeachingMessage : '') : strings.spoTeachingMessage;
  const notifyMessage = InfoMessage || teachingMessage;

  const commentString = Number(commentCount) > 1 ? strings.pageComments : strings.pageComment;
  const likeString = Number(likeCount) > 1 ? strings.pageLikes : strings.pageLike;
  const commentLabel = commentCount ? `${StringHelper.format(commentString, commentCount)}` : '';
  const likeLabel = likeCount ? `${StringHelper.format(likeString, likeCount)}` : '';
  // disable like click for now since we need build like panel
  const commentElem = commentLabel && createLikeCommentLink(commentLabel, /** onclick */undefined, pageUrl);

  let viewCountString = viewCount > 1 ? strings.views : strings.view;
  let viewerCountString = viewerCount > 1 ? strings.viewers : strings.viewer;

  const statsElem =
    isCompact ?
      (isLink ?
        (
          <div className={ `${AnalyticsStatsStyle.msAnalyticsStatsContainer}` }>
            <Link
              className={ `${AnalyticsStatsStyle.msAnalyticsStatsStatsStatsLink}` }
              onClick={ onClick }
            >
              <ActivityItem
                activityPersonas={
                  (activities && activities.length !== 0)
                    ? activities.slice(0, 3).map((activity: IActivity) => {
                      return {
                        imageUrl: activity && activity.actor && activity.actor.user && activity.actor.user.image
                      };
                    }).reverse()
                    : undefined
                }
                activityDescriptionText={ `${viewCount} ${viewCountString}` }
                isCompact={ true }
              />
            </Link>
            {
              likeLabel && itemKey ?
                <SocialBarLikeLink
                  likeLabel={ likeLabel }
                  itemKey={ itemKey }
                /> : null
            }
            {
              commentElem ? commentElem : null
            }
          </div>
        ) :
        (
          <div className={ `${AnalyticsStatsStyle.msAnalyticsStatsViewsNoLink}` }>
            { `${viewCount} ${viewCountString}` }
            <span className={ `${AnalyticsStatsStyle.msAnalyticsStatsViewsDividerNoDot}` } />
            { `${viewerCount} ${viewerCountString}` }
            { notifyMessage &&
              <TooltipHost content={ notifyMessage }>
                <Icon
                  className={ `${AnalyticsStatsStyle.msAnalyticsStatsTeachingMessage}` }
                  iconName={ 'Info' }
                  aria-describedby={ notifyMessage }
                />
              </TooltipHost> }
          </div>
        )
      )
      :
      (
        <div className={ `${AnalyticsStatsStyle.msAnalyticsStatsViews}` }>
          { `${viewerCount} ${viewerCountString}` }
          <span className={ `${AnalyticsStatsStyle.msAnalyticsStatsViewsDivider}` } />
          { `${viewCount} ${viewCountString}` }
          { InfoMessage && (
            <TooltipHost content={ InfoMessage }>
              <Icon
                className={ `${AnalyticsStatsStyle.msAnalyticsStatsInfo}` }
                iconName={ 'Info' }
                aria-describedby={ InfoMessage }
              />
            </TooltipHost>) }
        </div >
      );

  return statsElem;
};

AnalyticsStats.defaultProps = {
  withFacePile: false
};

export function getMessageSection(iconName: string, content: string, iconStyle?: IStyle): JSX.Element {
  const baseIconStyle: IStyle = {
    paddingRight: '8px',
    position: 'relative'
  };

  return (
    <div className={ `${AnalyticsStatsStyle.msAnalyticsStatsErrorMessage}` }>
      <Icon
        iconName={ iconName }
        className={ `${AnalyticsStatsStyle.msFileHoverCardErrorIcon}` }
        styles={ {
          root: mergeStyles([
            baseIconStyle,
            iconStyle
          ])
        } }
      />
      { content }
    </div>
  );
}

function createLikeCommentLink(label: string, onClick?: (ev: React.MouseEvent<HTMLElement>) => void, link?: string): JSX.Element {
  const textEle = (
    <ActivityItem
      activityDescription={
        <div className='ms-AnalyticsStats--stats--label'>
          { <div className='ms-AnalyticsStats--stats--count like'>{ label }</div> }
        </div>
      }
      isCompact={ true }
    />
  );

  // create link or plain text with activity item style
  return (!onClick && !link) ?
    (
      <div className={ `${AnalyticsStatsStyle.msAnalyticsStatsStatsNoLink}` }>
        {
          textEle
        }
      </div>
    ) : (
      <Link
        className={ `${AnalyticsStatsStyle.msAnalyticsStatsStatsStatsLink}` }
        href={ `${link}#comments` }
        target={ '_blank' }
        onClick={ onClick }
      >
        {
          textEle
        }
      </Link>
    );
}